import React, { useCallback, useState } from 'react';
import { Box, Flex, Text, Icon, Input, Spinner } from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FileUploadIcon } from '@spotoninc/nexus-web-assets';
import '../../styles/chakra-uploader.css';

/**
 * ChakraUploader - A custom file uploader component using Chakra UI
 *
 * @param {Object} props - Component props
 * @param {Function} props.onFileAccepted - Callback when files are accepted
 * @param {string} props.fileFormat - File format description (e.g., "Excel files")
 * @param {string} props.sizeConstraint - Size constraint description (e.g., "Max 5MB")
 * @param {Object} props.accept - Accepted file types object for react-dropzone
 * @param {number} props.maxSize - Maximum file size in bytes
 * @param {boolean} props.multiple - Whether to accept multiple files
 * @param {string} props.height - Height of the dropzone
 * @returns {JSX.Element} ChakraUploader component
 */
const ChakraUploader = ({
  onFileAccepted,
  fileFormat = '.xlsx, .xls, .csv',
  sizeConstraint = '5MB',
  accept = {
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xlsx',
    ],
    'text/csv': ['.csv'],
  },
  maxSize = 5242880, // 5MB
  multiple = false,
  height = '120px',
}) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(
    acceptedFiles => {
      setFiles(acceptedFiles);

      if (acceptedFiles.length > 0 && onFileAccepted) {
        setIsUploading(true);
        // Call the onFileAccepted callback and handle the promise
        Promise.resolve(onFileAccepted(acceptedFiles))
          .then(() => {
            // Reset files and uploading state after successful upload
            setFiles([]);
            setIsUploading(false);
          })
          .catch(error => {
            // Just log the error to console
            console.error('Error uploading file:', error);
            setIsUploading(false);
          });
      }
    },
    [onFileAccepted]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple,
    onDropRejected: rejectedFiles => {
      // Just log rejected files to console
      console.error(
        'Files rejected:',
        rejectedFiles
          .map(rejection => {
            const { file, errors } = rejection;
            return `${file.name}: ${errors.map(e => e.message).join(', ')}`;
          })
          .join('; ')
      );
    },
  });

  return (
    <Box width="100%">
      <Flex
        {...getRootProps()}
        direction="column"
        align="center"
        justify="center"
        py={6}
        px={4}
        height={height}
        className={`chakra-uploader ${isDragActive ? 'chakra-uploader-active' : ''}`}
        cursor={isUploading ? 'default' : 'pointer'}
        pointerEvents={isUploading ? 'none' : 'auto'}
      >
        <Input {...getInputProps()} disabled={isUploading} />

        {isUploading ? (
          <>
            <Spinner
              size="md"
              color="blue.500"
              thickness="3px"
              speed="0.65s"
              mb={3}
            />
            <Text className="chakra-uploader-text">Uploading...</Text>
          </>
        ) : (
          <>
            <Icon
              as={FileUploadIcon}
              boxSize="24px"
              mb={3}
              className="chakra-uploader-icon"
            />
            <Text className="chakra-uploader-text">
              Drop files here or{' '}
              <span className="chakra-uploader-link">select files</span>
            </Text>
          </>
        )}
      </Flex>
      <Text className="chakra-uploader-constraint" mt={2}>
        Files must be in {fileFormat} and {sizeConstraint}.
      </Text>
    </Box>
  );
};

export default ChakraUploader;
